/**
 * TenantSwitcher — the nav-mounted active-tenant control (tenant-foundation
 * Task 11, Req 19.1–19.3; the "Slack workspace switcher" pattern).
 *
 * Behavior by membership count (the switcher "lights up" only with >1 tenant):
 *   - 0 memberships / still loading → renders nothing (no tenant chrome).
 *   - 1 membership                  → a quiet, non-interactive tenant label.
 *   - >1 memberships                → a dropdown listing every membership; the
 *     active one is checked, a `suspended` tenant or `invited` standing is shown
 *     but disabled (not switchable), and selecting another calls
 *     `switchTenant` (server re-mint + token refresh, via AuthContext).
 *
 * It also performs the one-shot POST-LOGIN RESOLUTION (Req 19.1): if the viewer
 * holds Memberships but their active-tenant claim is absent or stale (names no
 * held Membership — e.g. a token minted before they joined), it switches to the
 * most-recently-active landable default so they "drop into" their tenant. This
 * is a no-op for the common case where the claim is already set.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { Briefcase, Check, ChevronDown } from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import { useMyMemberships } from '@/hooks/useMyMemberships';
import {
  isSwitchableMembership,
  resolveActiveTenant,
  type MembershipSummary,
} from '@/lib/tenantResolution';

export function TenantSwitcher() {
  const { user, switchTenant } = useAuth();
  const { memberships, status, refetch } = useMyMemberships();
  const [open, setOpen] = useState(false);
  const [switchingTo, setSwitchingTo] = useState<string | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const autoResolvedRef = useRef(false);

  const activeTenantId = user?.activeTenantId;

  // One-shot post-login resolution (Req 19.1): establish/repair the claim when
  // it names no held Membership. Fires at most once per mount and only when a
  // switch is actually needed, so it never loops.
  useEffect(() => {
    if (autoResolvedRef.current) return;
    if (status !== 'resolved') return;
    const resolution = resolveActiveTenant(activeTenantId, memberships);
    if (resolution.kind === 'needs-switch') {
      autoResolvedRef.current = true;
      void switchTenant(resolution.tenantId).then((res) => {
        if (res.success) refetch();
      });
    }
  }, [status, memberships, activeTenantId, switchTenant, refetch]);

  // Close the dropdown on outside click (mirrors the TopNav About dropdown).
  useEffect(() => {
    if (!open) return;
    function handleClickOutside(event: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  const handleSelect = useCallback(
    async (m: MembershipSummary) => {
      if (m.tenantId === activeTenantId) {
        setOpen(false);
        return;
      }
      if (!isSwitchableMembership(m)) return;
      setSwitchingTo(m.tenantId);
      const res = await switchTenant(m.tenantId);
      setSwitchingTo(null);
      setOpen(false);
      if (res.success) refetch();
    },
    [activeTenantId, switchTenant, refetch],
  );

  // Nothing to show until the caller's memberships resolve.
  if (status !== 'resolved' || memberships.length === 0) return null;

  const active =
    memberships.find((m) => m.tenantId === activeTenantId) ?? memberships[0];

  // Single membership → quiet, non-interactive label ("lights up" only with >1).
  if (memberships.length === 1) {
    return (
      <div
        data-testid="tenant-switcher"
        data-membership-count={1}
        className="flex items-center gap-2 px-3 py-2 rounded-md bg-muted/50 text-foreground min-w-0"
      >
        <Briefcase className="w-4 h-4 shrink-0 text-muted-foreground" aria-hidden="true" />
        <span className="truncate text-sm font-medium" title={active.tenantName}>
          {active.tenantName}
        </span>
      </div>
    );
  }

  // Multiple memberships → interactive dropdown.
  return (
    <div
      ref={containerRef}
      data-testid="tenant-switcher"
      data-membership-count={memberships.length}
      className="relative"
    >
      <button
        type="button"
        data-testid="tenant-switcher-trigger"
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={`Active tenant: ${active.tenantName}. Switch tenant.`}
        className="flex items-center gap-2 w-full px-3 py-2 rounded-md bg-muted/50 hover:bg-muted text-foreground transition-colors min-w-0 cursor-pointer border-none"
      >
        <Briefcase className="w-4 h-4 shrink-0 text-muted-foreground" aria-hidden="true" />
        <span className="truncate text-sm font-medium flex-1 text-left" title={active.tenantName}>
          {active.tenantName}
        </span>
        <ChevronDown
          className={`w-4 h-4 shrink-0 text-muted-foreground transition-transform ${open ? 'rotate-180' : ''}`}
          aria-hidden="true"
        />
      </button>

      {open && (
        <div
          role="menu"
          data-testid="tenant-switcher-menu"
          className="absolute left-0 right-0 mt-1 z-50 bg-card border border-border rounded-md shadow-prominent overflow-hidden"
        >
          {memberships.map((m) => {
            const isActive = m.tenantId === active.tenantId;
            const switchable = isSwitchableMembership(m);
            const isSwitching = switchingTo === m.tenantId;
            return (
              <button
                key={m.tenantId}
                type="button"
                role="menuitem"
                data-testid={`tenant-switcher-item-${m.tenantId}`}
                disabled={!switchable || switchingTo !== null}
                aria-current={isActive ? 'true' : undefined}
                onClick={() => handleSelect(m)}
                className={[
                  'flex items-center gap-2 w-full px-3 py-2.5 text-left text-sm transition-colors border-none bg-transparent',
                  switchable
                    ? 'cursor-pointer hover:bg-muted text-foreground'
                    : 'cursor-not-allowed text-muted-foreground',
                ].join(' ')}
              >
                <span className="truncate flex-1" title={m.tenantName}>
                  {m.tenantName}
                  {m.tenantStatus === 'suspended' && (
                    <span className="ml-1 text-xs text-muted-foreground">(suspended)</span>
                  )}
                  {m.tenantStatus === 'active' && m.membershipStatus === 'invited' && (
                    <span className="ml-1 text-xs text-muted-foreground">(invited)</span>
                  )}
                </span>
                {isSwitching && (
                  <span className="text-xs text-muted-foreground shrink-0">Switching…</span>
                )}
                {isActive && !isSwitching && (
                  <Check
                    className="w-4 h-4 shrink-0 text-primary"
                    data-testid="tenant-switcher-active-check"
                    aria-hidden="true"
                  />
                )}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default TenantSwitcher;
